import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Language, Translations, LanguageOption } from "./types.js";
import { translations, SUPPORTED_LANGUAGES } from "./locales.js";

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  supportedLanguages: LanguageOption[];
  currentLanguageOption: LanguageOption;
}

const PRIMARY_STORAGE_KEY = "hp_language";
const LEGACY_STORAGE_KEY = "hp_helix_lang";

const I18nContext = createContext<I18nContextType | null>(null);

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const stored = (localStorage.getItem(PRIMARY_STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY)) as Language | null;
      if (stored && ["en", "fr", "ln", "sw"].includes(stored)) {
        return stored;
      }
      const browserLang = navigator.language.slice(0, 2);
      if (browserLang === "fr") return "fr";
      if (browserLang === "sw") return "sw";
      if (browserLang === "ln") return "ln";
    } catch {
      // Fallback in case of SSR or restricted storage
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(PRIMARY_STORAGE_KEY, lang);
      localStorage.setItem(LEGACY_STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    try {
      document.documentElement.lang = language;
    } catch {
      // ignore
    }
  }, [language]);

  const currentLanguageOption =
    SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];

  const t = translations[language] || translations.en;

  return (
    <I18nContext.Provider
      value={{
        language,
        setLanguage,
        t,
        supportedLanguages: SUPPORTED_LANGUAGES,
        currentLanguageOption,
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
